import { HttpClient, HttpParams, HttpStatusCode } from '@angular/common/http';
import {Injectable} from '@angular/core';
import {firstValueFrom, Observable} from 'rxjs';
import {GetPartners} from './GetPartners';
import {GetPartnersProjects} from "./GetPartnersProjects";
import {Cache} from '../Cache';
import {PostPartners} from './PostPartners';
import {PutPartners} from './PutPartners';
import {NotificationService} from '../other/notification.service';

@Injectable({
  providedIn: 'root'
})
export class PartnerService {

  private readonly partnersCache = new Cache<GetPartners>(element => element.id);
  private readonly projectsCache = new Cache<GetPartnersProjects[]>(element => element[0].partnerId);

  constructor(
    private notificationService: NotificationService,
    private client: HttpClient,
  ) {}

  /**
   * Emits changes in cached partners.
   * Changes to the cache are cause by:
   *
   * {@link fetchAllPartners()}
   *
   * {@link deletePartner()}
   *
   * {@link createPartner()}
   *
   * {@link updatePartner()}
   * @returns an observable of cached {@link GetPartners} as a list
   */
  observePartners(): Observable<GetPartners[]> {
    return this.partnersCache.observeAllElementsList();
  }

  /**
   * Calls the backend to get all {@link GetPartners}.
   *
   * Stores the data in the cache, which emits the change to {@link observePartners()}
   * @returns a promise with all {@link GetPartners}
   */
  fetchAllPartners(): Promise<GetPartners[]> {
    return this.partnersCache.putAll(firstValueFrom(this.client.get<GetPartners[]>('/api/partners')));
  }

  /**
   * Calls the backend to get a filtered {@link GetPartners} list.
   * @param partnerIds A list of all wanted partner ids
   * @returns a promise with all {@link GetPartners}
   */
  loadPartnersById(partnerIds: string[]): Promise<GetPartners[]> {
    return firstValueFrom(this.client.get<GetPartners[]>('/api/partners', {
      params: new HttpParams({
        fromObject: {
          partnerIds: partnerIds,
        }
      })
    }));
  }

  /**
   * Calls the backend to delete the partner with the given id.
   *
   * Triggers an emit event of {@link observePartners()}
   * @param partnerId Id of the partner you want to delete
   * @returns a promise with void or {@link HttpStatusCode.Unauthorized} | {@link HttpStatusCode.NotFound}
   *
   */
  async deletePartner(partnerId: string): Promise<void> {
    const promise = firstValueFrom(this.client.delete<void>('api/partners/' + partnerId));
    this.notificationService.handlePromise('Partner deleted!', 'Deleting Partner failed...', promise);
    promise.then(() => {
      this.partnersCache.invalidateOne(partnerId);
    }).catch(() => {/*nothing to do with failed request*/});
    await promise;
    return promise;
  }

  /**
   * Calls the backend to create a new partner. After that it calls the backend again to get a refreshed
   * list of all partners.
   *
   * Triggers an emit event of {@link observePartners()}
   * @param partner {@link PostPartners} is the partner you want to create
   * @returns a promise with the created {@link GetPartners} or {@link HttpStatusCode.Unauthorized}
   */
  async createPartner(partner: PostPartners): Promise<GetPartners> {
    const promise = firstValueFrom(this.client.post<GetPartners>('api/partners', partner));
    this.notificationService.handlePromise('Partner created!', 'Creating Partner failed...', promise);
    await promise.catch(() => {/*nothing to do with failed request*/});
    await this.fetchAllPartners();
    return promise;
  }

  /**
   * Calls the backend to update a partner.
   *
   * Triggers an emit event of {@link observePartners()}
   * @param id id of the partner you want to update
   * @param partner partner data you want to update
   * @returns a promise with the updated {@link GetPartners} or {@link HttpStatusCode.Unauthorized} | {@link HttpStatusCode.NotFound}
   */
  async updatePartner(id: string, partner: PutPartners) {
    const promise = firstValueFrom(this.client.put<GetPartners>(`api/partners/${id}`, partner));
    this.notificationService.handlePromise('Partner updated!', 'Updating Partner failed...', promise);
    await promise;
    return this.partnersCache.putOne(id, promise);
  }

  /**
   * Calls the backend to get a list of all {@link GetPartnersProjects} for the given id
   * @param partnerId get all {@link GetPartnersProjects} for this id
   * @returns a promise with {@link GetPartnersProjects} list or {@link HttpStatusCode.NotFound}
   */
  async fetchPartnerProjects(partnerId: string): Promise<GetPartnersProjects[]> {
    const promise = this.projectsCache.getOne(
      partnerId,
      id => firstValueFrom(this.client.get<GetPartnersProjects[]>(`/api/partners/${id}/projects`))
    );
    this.notificationService.handlePromiseError('Loading Partners failed...', promise);
    await promise.catch(() => {/*nothing to do with failed request*/});
    return promise;
  }

}
